import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Shield,
  Building2,
  Users,
  Heart,
  Scale,
  UserX,
  Camera,
} from "lucide-react";
import { AddressBlock } from "./AddressBlock";
import { ANREDEN } from "./options";
import { detectRechtsform } from "./detectRechtsform";
import { Person2Fields } from "./Person2Fields";
import { TitelSelect } from "./TitelSelect";
import { BeteiligteBlock } from "./BeteiligteBlock";
import { FallKategorie } from "./FallKategorie";
import { Gegenseite } from "./Gegenseite";
import { LegalRepSection } from "./LegalRepSection";
import { Modal } from "./Modal";
import { DSGVO_TEXT, LEGAL_REP_NOTICE } from "./legalTexts";
import { InsuranceSelect } from "./InsuranceSelect";
import { needsHaftpflicht, needsOpponentStep } from "./insurances";
import { clipDateYear } from "./dateUtils";
import { DateField } from "./DateField";
import { IdScannerModal } from "./IdScannerModal";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/adresseingabe`;

const emptyForm = (customer_type = "neu") => ({
  customer_type,
  anrede: "Herr",
  titel: "",
  vorname: "",
  name: "",
  geburtsdatum: "",
  strasse: "",
  hausnummer: "",
  plz: "",
  ort: "",
  country_code: "DE",
  mobiltelefon: "",
  festnetz: "",
  fax: "",
  email: "",
  // Firma
  rechtsform: "",
  register_art: "",
  register_nummer: "",
  registergericht: "",
  vertreter: "",
  vertreter_strasse: "",
  vertreter_hausnummer: "",
  vertreter_plz: "",
  vertreter_ort: "",
  vertreter_country_code: "DE",
  // RSV
  rsv_active: false,
  rsv_nr: "",
  rsv_name: "",
  rsv_versicherungsnummer: "",
  rsv_schadennummer: "",
  // Haftpflicht (nur bei bestimmten Rechtsgebieten relevant)
  haftpflicht_active: false,
  haftpflicht_name: "",
  haftpflicht_aktenzeichen: "",
  // Anliegen — kurze Beschreibung des Mandanten
  anliegen_beschreibung: "",
  // Aktenzeichen (z. B. Gericht, Polizei, Behörde)
  aktenzeichen: "",
  aktenzeichen_typ: "",
  aktenzeichen_stelle: "",
  // Weitere Beteiligte am Sachverhalt
  weitere_beteiligte: [],
  // Ausweis-Scan (Base64-JPEG)
  ausweis_vorderseite: "",
  ausweis_rueckseite: "",
  // Eheleute - Person 2 Ausweis
  ausweis2_vorderseite: "",
  ausweis2_rueckseite: "",
  // Eheleute - Person 2
  partner_person: {
    titel: "",
    vorname: "",
    name: "",
    geburtsdatum: "",
    use_main_address: true,
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
    country_code: "DE",
  },
  // Gesetzlicher Vertreter (Betreuer, Bevollmächtigter, ...)
  gesetzlich_vertreten: false,
  legal_rep: {
    anrede: "Herr",
    titel: "",
    vorname: "",
    name: "",
    geburtsdatum: "",
    use_main_address: true,
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
    country_code: "DE",
    mobiltelefon: "",
    festnetz: "",
    fax: "",
    email: "",
  },
  // Fall-Kategorie
  case_info: { category: "", subcategory: "", sonstige_text: "" },
  // Gegnerseite
  opponent: {
    bekannt: false,
    is_firma: false,
    anrede: "",
    titel: "",
    vorname: "",
    name: "",
    geburtsdatum: "",
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
    country_code: "DE",
    mobiltelefon: "",
    festnetz: "",
    fax: "",
    email: "",
    rechtsform: "",
    register_art: "",
    register_nummer: "",
    registergericht: "",
    vertreter: "",
    vertreter_strasse: "",
    vertreter_hausnummer: "",
    vertreter_plz: "",
    vertreter_ort: "",
    vertreter_country_code: "DE",
    lawyer_known: false,
    lawyer_name: "",
  },
});

export const AdresseingabeForm = ({ token, customerType, onUnauthorized, onBack }) => {
  const [form, setForm] = useState(emptyForm(customerType));
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [formStep, setFormStep] = useState(1); // 1 Stammdaten · 2 Anliegen · 3 Gegenseite · 4 Abschluss/Ausweis
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
  const [showLegalRepNotice, setShowLegalRepNotice] = useState(false);
  const [showDsgvo, setShowDsgvo] = useState(false);
  const [showIdScanner, setShowIdScanner] = useState(false);
  const [showMissingIdModal, setShowMissingIdModal] = useState(false);
  const [idLater, setIdLater] = useState(false);

  const isFirma = form.anrede === "Firma";
  const isEheleute = form.anrede === "Eheleute";
  const isBestand = customerType === "bestand_geaendert";
  const isUnveraendert = customerType === "bestand_unveraendert";

  // Conditional UX based on case category / subcategory
  const showHaftpflicht = needsHaftpflicht(form.case_info?.category);
  const hasCategory = Boolean(form.case_info?.category);
  const showOpponentStep = needsOpponentStep(
    form.case_info?.category,
    form.case_info?.subcategory,
  );

  // Auto-detect Rechtsform from Firmenname when Anrede=Firma
  useEffect(() => {
    if (isFirma && form.name) {
      const rf = detectRechtsform(form.name);
      if (rf && rf !== form.rechtsform) {
        setForm((f) => ({ ...f, rechtsform: rf }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, isFirma]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setMany = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      toast.error("Sitzung abgelaufen — bitte erneut anmelden.");
      onUnauthorized?.();
      return true;
    }
    return false;
  };

  const searchPlaces = async (q) => {
    try {
      const { data } = await axios.get(`${API}/places`, {
        ...authHeaders,
        params: { q },
      });
      return data || [];
    } catch (err) {
      handleAuthError(err);
      return [];
    }
  };

  const mainAddress = {
    strasse: form.strasse,
    hausnummer: form.hausnummer,
    plz: form.plz,
    ort: form.ort,
    country_code: form.country_code,
  };
  const onMainAddressChange = (next) => setMany(next);

  const vertreterAddress = {
    strasse: form.vertreter_strasse,
    hausnummer: form.vertreter_hausnummer,
    plz: form.vertreter_plz,
    ort: form.vertreter_ort,
    country_code: form.vertreter_country_code,
  };
  const onVertreterAddressChange = (next) =>
    setMany({
      vertreter_strasse: next.strasse,
      vertreter_hausnummer: next.hausnummer,
      vertreter_plz: next.plz,
      vertreter_ort: next.ort,
      vertreter_country_code: next.country_code,
    });

  // Validate step 1 — Stammdaten (or minimal form for unveraendert)
  const validateStep1 = () => {
    if (isUnveraendert) {
      const req = [
        ["anrede", "Anrede"],
        ["name", isFirma ? "Firmenname" : "Name"],
        ["strasse", "Straße"],
        ["hausnummer", "Hausnummer"],
        ["plz", "PLZ"],
        ["ort", "Ort"],
      ];
      if (!isFirma && !isEheleute) req.splice(1, 0, ["vorname", "Vorname"]);
      if (isEheleute) {
        req.splice(1, 0, ["vorname", "Vorname (Person 1)"]);
        const p2 = form.partner_person || {};
        if (!p2.vorname || !p2.name) {
          toast.error("Bitte Vorname und Name von Person 2 ausfüllen.");
          return false;
        }
      }
      for (const [k, label] of req) {
        if (!form[k] || String(form[k]).trim() === "") {
          toast.error(`Bitte ${label} ausfüllen.`);
          return false;
        }
      }
      return true;
    }

    const reqCommon = [
      ["anrede", "Anrede"],
      ["name", isFirma ? "Firmenname" : "Name"],
      ["strasse", "Straße"],
      ["hausnummer", "Hausnummer"],
      ["plz", "PLZ"],
      ["ort", "Ort"],
      ["mobiltelefon", "Telefon"],
      ["email", "E-Mail"],
      ["geburtsdatum", isFirma ? "Geburtsdatum des Vertreters" : "Geburtsdatum"],
    ];
    if (!isFirma) reqCommon.splice(1, 0, ["vorname", "Vorname"]);
    if (isFirma) reqCommon.push(["vertreter", "Vertreten durch"]);
    if (isBestand && !isFirma) reqCommon.push(["titel", "Titel"]);
    if (isBestand && isFirma) {
      reqCommon.push(["registergericht", "Registergericht"]);
      reqCommon.push(["register_art", "Register-Art"]);
      reqCommon.push(["register_nummer", "Registernummer"]);
    }
    for (const [k, label] of reqCommon) {
      if (!form[k] || String(form[k]).trim() === "") {
        toast.error(`Bitte ${label} ausfüllen.`);
        return false;
      }
    }
    if (isEheleute) {
      const p2 = form.partner_person || {};
      const reqP2 = [
        ["vorname", "Vorname (Person 2)"],
        ["name", "Name (Person 2)"],
        ["geburtsdatum", "Geburtsdatum (Person 2)"],
      ];
      for (const [k, label] of reqP2) {
        if (!p2[k] || String(p2[k]).trim() === "") {
          toast.error(`Bitte ${label} ausfüllen.`);
          return false;
        }
      }
    }
    if (form.gesetzlich_vertreten && !isFirma) {
      const rep = form.legal_rep || {};
      const reqRep = [
        ["anrede", "Anrede des Vertreters"],
        ["vorname", "Vorname des Vertreters"],
        ["name", "Name des Vertreters"],
        ["geburtsdatum", "Geburtsdatum des Vertreters"],
      ];
      for (const [k, label] of reqRep) {
        if (!rep[k] || String(rep[k]).trim() === "") {
          toast.error(`Bitte ${label} ausfüllen.`);
          return false;
        }
      }
      if (!rep.use_main_address) {
        const reqAddr = [
          ["strasse", "Straße des Vertreters"],
          ["hausnummer", "Hausnummer des Vertreters"],
          ["plz", "PLZ des Vertreters"],
          ["ort", "Ort des Vertreters"],
        ];
        for (const [k, label] of reqAddr) {
          if (!rep[k] || String(rep[k]).trim() === "") {
            toast.error(`Bitte ${label} ausfüllen.`);
            return false;
          }
        }
      }
    }
    if (form.rsv_active) {
      if (!form.rsv_nr && !form.rsv_name.trim()) {
        toast.error("Bitte Versicherung aus der Liste auswählen.");
        return false;
      }
      if (!form.rsv_versicherungsnummer.trim()) {
        toast.error("Bitte Versicherungsnummer angeben.");
        return false;
      }
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Bitte gültige E-Mail-Adresse angeben.");
      return false;
    }
    return true;
  };

  const goToStep = (n) => {
    if (n > formStep) {
      if (formStep === 1 && !validateStep1()) return;
    }
    // Skip Step 3 (Gegenseite) when not relevant
    let target = n;
    if (!showOpponentStep) {
      if (n === 3 && formStep === 2) target = 4;
      if (n === 3 && formStep === 4) target = 2;
    }
    // Clamp
    if (target < 1) target = 1;
    if (target > 4) target = 4;
    setFormStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const idsMissing = () => {
    if (isFirma) return false;
    const hasMandantIds = Boolean(
      (form.ausweis_vorderseite || "").trim() &&
      (form.ausweis_rueckseite || "").trim(),
    );
    if (isEheleute) {
      const hasPartnerIds = Boolean(
        (form.ausweis2_vorderseite || "").trim() &&
        (form.ausweis2_rueckseite || "").trim(),
      );
      return !(hasMandantIds && hasPartnerIds);
    }
    return !hasMandantIds;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) {
      setFormStep(1);
      return;
    }
    if (!datenschutzAccepted) {
      toast.error("Bitte Datenschutzerklärung bestätigen.");
      return;
    }
    if (idsMissing() && !idLater) {
      setShowMissingIdModal(true);
      return;
    }
    setSubmitting(true);
    try {
      // Auto-derive opponent.bekannt = any field filled
      const opp = form.opponent || {};
      const anyOpp = Boolean(
        (opp.name || "").trim() ||
        (opp.vorname || "").trim() ||
        (opp.strasse || "").trim() ||
        (opp.plz || "").trim() ||
        (opp.ort || "").trim() ||
        (opp.email || "").trim() ||
        (opp.mobiltelefon || "").trim()
      );
      // If opponent step is skipped (Strafrecht, Vertragsprüfung/-erstellung),
      // send a blank opponent regardless of what user might have entered earlier.
      const finalOpp = showOpponentStep
        ? { ...opp, bekannt: opp.bekannt || anyOpp }
        : { bekannt: false, is_firma: false, lawyer_known: false, lawyer_name: "" };
      const payload = {
        ...form,
        opponent: finalOpp,
        datenschutz_accepted: datenschutzAccepted,
        ausweis_nachreichen: idLater && idsMissing(),
      };
      const { data } = await axios.post(`${API}/submit`, payload, authHeaders);
      setDone(data);
      if (data.email_sent) {
        toast.success("Daten erfolgreich übermittelt.");
      } else {
        toast.message("Daten erfasst.", { description: data.reason });
      }
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return <SuccessScreen data={done} isBestand={isBestand} onReset={onBack} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <div className="flex items-center gap-3 text-[#505a74] mb-5">
        <span className="section-mark" />
        <span className="eyebrow">
          {isBestand
            ? "Bestandsänderung"
            : isUnveraendert
              ? "Bestandsmandant · neues Anliegen"
              : "Mandantenaufnahme"}
        </span>
      </div>
      <h1
        data-testid="form-title"
        className="font-display font-bold text-[40px] md:text-[56px] leading-[0.95] tracking-tight"
      >
        {isBestand
          ? "Daten aktualisieren."
          : isUnveraendert
            ? "Neues Anliegen."
            : "Adresseingabe."}
      </h1>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          data-testid="form-back"
          className="mt-4 text-[12px] font-semibold link-underline inline-flex items-center gap-1.5 text-[#505a74]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Auswahl ändern
        </button>
      )}

      {/* Wizard-progress */}
      <StepProgress
        current={formStep}
        labels={[
          "Stammdaten",
          "Anliegen",
          showOpponentStep ? "Gegenseite" : "—",
          "Abschluss",
        ]}
      />

      {/* Datenschutz */}
      <div className="mt-8 p-6 md:p-7 bg-white border border-[#e8e6df] rounded-sm">
        <div className="flex gap-4 items-start">
          <Shield className="w-5 h-5 text-[#d90429] mt-0.5 shrink-0" />
          <div className="text-[14px] text-[#505a74] leading-relaxed">
            <strong className="text-[#0a1128]">
              Vertraulichkeit & Datenschutz.
            </strong>{" "}
            Sämtliche von Ihnen übermittelten Angaben werden ausschließlich zur
            Anbahnung bzw. Bearbeitung Ihres Mandats verwendet, streng
            vertraulich behandelt und unter Beachtung der anwaltlichen
            Verschwiegenheitspflicht (§&nbsp;43a&nbsp;BRAO) sowie der
            Datenschutz-Grundverordnung (DSGVO) verarbeitet.
          </div>
        </div>
      </div>

      {/* Ausweis-Hinweis — nur in Step 1 (Stammdaten) und nur für Privatpersonen */}
      {formStep === 1 && !isFirma && (
        <div
          data-testid="ausweis-hinweis"
          className="mt-4 p-4 md:p-5 bg-[#fff8e6] border border-[#f0d77a] rounded-sm flex gap-3 items-start"
        >
          <Camera className="w-5 h-5 text-[#a16b00] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#5b4500] leading-relaxed">
            <strong className="text-[#0a1128]">Bitte Ausweis bereithalten.</strong>{" "}
            Im letzten Schritt nehmen wir je ein Foto der Vorder- und Rückseite
            Ihres Personalausweises (oder Reisepasses) auf. Halten Sie das
            Dokument griffbereit.
          </div>
        </div>
      )}

      <form
        onSubmit={submit}
        noValidate
        data-testid="adresseingabe-form"
        className="mt-10 space-y-10"
      >
        {formStep === 1 && (
        <>
        {(() => {
          let _stepNum = 0;
          const step = () => String(++_stepNum).padStart(2, "0");
          return (
            <>
        <Fieldset
          title={isFirma ? "Firmenangaben" : (isEheleute ? "Person 1" : "Persönliche Angaben")}
          step={step()}
        >
          {!isFirma ? (
            <>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <AnredeField value={form.anrede} onChange={set("anrede")} />
              </div>
              <TitelSelect
                id="titel"
                label={isBestand ? "Titel *" : "Titel"}
                value={form.titel}
                onChange={set("titel")}
              />
              <div />
              <Field
                id="vorname"
                label="Vorname *"
                value={form.vorname}
                onChange={set("vorname")}
              />
              <Field
                id="name"
                label="Name *"
                value={form.name}
                onChange={set("name")}
              />
              <DateField
                id="geburtsdatum"
                label="Geburtsdatum"
                required
                value={form.geburtsdatum}
                onChange={set("geburtsdatum")}
                className="sm:col-span-2"
              />
            </div>

            {/* Gesetzlich vertreten? */}
            <div className="mt-6 pt-6 border-t border-[#e8e6df]">
              <label
                className="flex items-start gap-3 cursor-pointer select-none"
                data-testid="legal-rep-checkbox-label"
              >
                <input
                  type="checkbox"
                  data-testid="form-input-gesetzlich-vertreten"
                  checked={form.gesetzlich_vertreten}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Open notice modal — only confirm via OK toggles the box
                      setShowLegalRepNotice(true);
                    } else {
                      setForm((f) => ({ ...f, gesetzlich_vertreten: false }));
                    }
                  }}
                  className="mt-1 w-4 h-4 accent-[#d90429] shrink-0"
                />
                <span className="text-[14px] text-[#0a1128]">
                  <strong>Ich bin gesetzlich vertreten.</strong>
                  <span className="block text-[#505a74] text-[13px] mt-0.5">
                    Aktivieren, wenn die Beauftragung durch einen gesetzlichen
                    Vertreter, Betreuer oder Bevollmächtigten erfolgt.
                  </span>
                </span>
              </label>
            </div>
            </>
          ) : (
            <FirmaSection01
              form={form}
              set={set}
              mainAddress={mainAddress}
              onMainAddressChange={onMainAddressChange}
              searchPlaces={searchPlaces}
            />
          )}
        </Fieldset>

        {form.gesetzlich_vertreten && !isFirma && (
          <Fieldset
            title="Gesetzlicher Vertreter"
            step={step()}
            icon={<Users className="w-4 h-4 text-[#d90429]" />}
          >
            <LegalRepSection
              value={form.legal_rep}
              onChange={(next) => setMany({ legal_rep: next })}
              searchPlaces={searchPlaces}
            />
          </Fieldset>
        )}

        {isFirma ? (
          <Fieldset
            title="Vertreten durch"
            step={step()}
            icon={<Users className="w-4 h-4 text-[#d90429]" />}
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                id="vertreter"
                label="Name des Vertreters *"
                value={form.vertreter}
                onChange={set("vertreter")}
                placeholder="z. B. Max Mustermann"
                className="sm:col-span-2"
              />
              <DateField
                id="geburtsdatum"
                label="Geburtsdatum des Vertreters"
                required
                value={form.geburtsdatum}
                onChange={set("geburtsdatum")}
                className="sm:col-span-2"
              />
            </div>
            <div className="mt-6 pt-6 border-t border-[#e8e6df]">
              <div className="eyebrow text-[#505a74] mb-2">
                Anschrift des Vertreters
              </div>
              <p className="text-[12px] text-[#505a74] mb-4">
                (falls abweichend von der Firmenanschrift — sonst freilassen)
              </p>
              <AddressBlock
                value={vertreterAddress}
                onChange={onVertreterAddressChange}
                search={searchPlaces}
                idPrefix="form-vertreter-address"
                searchPlaceholder="Anschrift des Vertreters"
              />
            </div>
          </Fieldset>
        ) : (
          <Fieldset title="Adresse" step={step()}>
            <AddressBlock
              value={mainAddress}
              onChange={onMainAddressChange}
              search={searchPlaces}
              idPrefix="form-address"
              searchPlaceholder="z. B. Hauptstraße 79, Lohmar"
            />
          </Fieldset>
        )}

        {isEheleute && (
          <Fieldset
            title="Person 2"
            step={step()}
            icon={<Heart className="w-4 h-4 text-[#d90429]" />}
          >
            <Person2Fields
              value={form.partner_person}
              onChange={(next) => setMany({ partner_person: next })}
              searchPlaces={searchPlaces}
            />
          </Fieldset>
        )}

        {!isUnveraendert && (
        <Fieldset title="Kontakt" step={step()}>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              id="mobiltelefon"
              type="tel"
              label="Telefon *"
              value={form.mobiltelefon}
              onChange={set("mobiltelefon")}
              placeholder="z. B. 0170 ..."
            />
            <Field
              id="festnetz"
              type="tel"
              label="Festnetz"
              value={form.festnetz}
              onChange={set("festnetz")}
              placeholder="optional"
            />
            <Field
              id="fax"
              type="tel"
              label="Fax"
              value={form.fax}
              onChange={set("fax")}
              placeholder="optional"
            />
            <Field
              id="email"
              type="email"
              label="E-Mail *"
              value={form.email}
              onChange={set("email")}
              placeholder="ihre@email.de"
            />
          </div>
        </Fieldset>
        )}

        {!isUnveraendert && (
        <Fieldset title="Rechtsschutzversicherung" step={step()}>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="form-input-rsv-active"
              checked={form.rsv_active}
              onChange={(e) => set("rsv_active")(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#d90429]"
            />
            <span className="text-[14px] text-[#0a1128]">
              <strong>Eine Rechtsschutzversicherung besteht.</strong>
              <span className="block text-[#505a74] text-[13px] mt-0.5">
                Bitte aktivieren, wenn Sie eine RSV in Anspruch nehmen möchten.
              </span>
            </span>
          </label>
          {form.rsv_active && (
            <div
              data-testid="form-rsv-fields"
              className="mt-6 space-y-5"
            >
              <div>
                <span className="eyebrow text-[#505a74]">
                  Versicherung auswählen *
                </span>
                <div className="mt-2">
                  <InsuranceSelect
                    mode="rsv"
                    selectedNr={form.rsv_nr}
                    selectedName={form.rsv_name}
                    onSelect={(sel) => {
                      if (sel) {
                        setMany({ rsv_nr: sel.nr, rsv_name: sel.name });
                      } else {
                        setMany({ rsv_nr: "", rsv_name: "" });
                      }
                    }}
                    idPrefix="rsv"
                    placeholder="z. B. ARAG, ADAC, Allianz …"
                  />
                </div>
                <p className="mt-2 text-[11px] text-[#505a74]">
                  Geben Sie den Namen Ihrer Rechtsschutzversicherung ein —
                  die Auswahl wird automatisch der Kanzleisoftware zugeordnet.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  id="rsv-versicherungsnummer"
                  label="Versicherungsnummer *"
                  value={form.rsv_versicherungsnummer}
                  onChange={set("rsv_versicherungsnummer")}
                />
                <Field
                  id="rsv-schadennummer"
                  label="Schadennummer"
                  value={form.rsv_schadennummer}
                  onChange={set("rsv_schadennummer")}
                  placeholder="falls bereits vergeben"
                />
              </div>
            </div>
          )}
        </Fieldset>
        )}
            </>
          );
        })()}
        </>
        )}

        {formStep === 2 && (
          <>
            <Fieldset
              title="Ihr Anliegen"
              step="01"
              icon={<Scale className="w-4 h-4 text-[#d90429]" />}
            >
              <FallKategorie
                value={form.case_info}
                onChange={(next) => setMany({ case_info: next })}
              />
            </Fieldset>

            {hasCategory && (
              <Fieldset
                title="Worum geht es?"
                step="02"
                icon={<Shield className="w-4 h-4 text-[#d90429]" />}
              >
                <p className="text-[13px] text-[#505a74] leading-relaxed mb-4">
                  Beschreiben Sie kurz Ihr Anliegen in eigenen Worten — ein
                  paar Stichpunkte oder Sätze reichen.
                </p>
                <textarea
                  data-testid="form-input-anliegen-beschreibung"
                  value={form.anliegen_beschreibung || ""}
                  onChange={(e) => setMany({ anliegen_beschreibung: e.target.value })}
                  placeholder={'z. B. "Schreiben der Gegenseite erhalten, Frist läuft am …", "Streit mit Vermieter wegen Nebenkostenabrechnung", "Kündigungsschutzklage" …'}
                  rows={4}
                  maxLength={1500}
                  className="block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[14px] leading-relaxed focus:outline-none focus:border-[#0a1128] transition resize-y"
                />
                <p className="text-[11px] text-[#a8a89a] mt-2 text-right">
                  {(form.anliegen_beschreibung || "").length} / 1500
                </p>
              </Fieldset>
            )}

            {hasCategory && (
              <Fieldset
                title="Aktenzeichen (optional)"
                step="03"
                icon={<Shield className="w-4 h-4 text-[#d90429]" />}
              >
                <p className="text-[13px] text-[#505a74] leading-relaxed mb-4">
                  Falls Ihnen bereits ein Aktenzeichen vorliegt (z. B. von
                  Gericht, Polizei oder Behörde), tragen Sie es bitte hier ein.
                </p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    id="aktenzeichen"
                    label="Aktenzeichen"
                    value={form.aktenzeichen}
                    onChange={set("aktenzeichen")}
                    placeholder="z. B. 123 Js 4567/24"
                  />
                  <label className="block">
                    <span className="eyebrow text-[#505a74]">Zuordnung</span>
                    <div className="relative mt-2">
                      <select
                        data-testid="form-input-aktenzeichen-typ"
                        value={form.aktenzeichen_typ || ""}
                        onChange={(e) => setMany({ aktenzeichen_typ: e.target.value })}
                        className="block w-full appearance-none bg-white border border-[#e8e6df] rounded-sm px-4 py-3 pr-10 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
                      >
                        <option value="">— bitte wählen —</option>
                        <option value="Gericht">Gericht</option>
                        <option value="Polizei">Polizei</option>
                        <option value="Staatsanwaltschaft">Staatsanwaltschaft</option>
                        <option value="Bußgeldstelle">Bußgeldstelle</option>
                        <option value="Behörde">Behörde / Amt</option>
                        <option value="Versicherung">Versicherung</option>
                        <option value="Sonstige">Sonstige</option>
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#505a74]">▾</span>
                    </div>
                  </label>
                  {form.aktenzeichen_typ && (
                    <Field
                      id="aktenzeichen-stelle"
                      label={
                        form.aktenzeichen_typ === "Gericht"
                          ? "Welches Gericht?"
                          : form.aktenzeichen_typ === "Polizei"
                            ? "Welche Dienststelle?"
                            : form.aktenzeichen_typ === "Sonstige"
                              ? "Ausstellende Stelle"
                              : `Welche ${form.aktenzeichen_typ}?`
                      }
                      value={form.aktenzeichen_stelle}
                      onChange={set("aktenzeichen_stelle")}
                      placeholder="Name der Stelle / des Gerichts"
                      className="sm:col-span-2"
                    />
                  )}
                </div>
              </Fieldset>
            )}

            {hasCategory && (
              <Fieldset
                title="Weitere Beteiligte am Sachverhalt"
                step="04"
                icon={<Users className="w-4 h-4 text-[#d90429]" />}
              >
                <BeteiligteBlock
                  value={form.weitere_beteiligte}
                  onChange={(next) => setMany({ weitere_beteiligte: next })}
                />
              </Fieldset>
            )}
          </>
        )}

        {formStep === 3 && (
          <>
            {showOpponentStep ? (
              <Fieldset
                title="Gegenseite"
                step="01"
                icon={<UserX className="w-4 h-4 text-[#d90429]" />}
              >
                <Gegenseite
                  value={form.opponent}
                  onChange={(next) => setMany({ opponent: next })}
                  searchPlaces={searchPlaces}
                />
              </Fieldset>
            ) : (
              <Fieldset
                title="Hinweis"
                step="01"
                icon={<UserX className="w-4 h-4 text-[#505a74]" />}
              >
                <p className="text-[14px] text-[#0a1128] leading-relaxed">
                  Bei Ihrer gewählten Fallkategorie{" "}
                  <strong>
                    {form.case_info?.category}
                    {form.case_info?.subcategory
                      ? ` · ${form.case_info.subcategory}`
                      : ""}
                  </strong>{" "}
                  ist die Angabe einer Gegenseite zunächst nicht erforderlich.
                </p>
              </Fieldset>
            )}

            {showHaftpflicht && (
              <Fieldset
                title="Haftpflichtversicherung"
                step="02"
                icon={<Shield className="w-4 h-4 text-[#d90429]" />}
              >
                <p className="text-[13px] text-[#505a74] leading-relaxed mb-5">
                  Sollte in Ihrem Fall eine Haftpflichtversicherung beteiligt
                  sein (z. B. der Gegenseite oder eine eigene Berufs-/
                  Privathaftpflicht), können Sie diese hier angeben. Auch wenn
                  Sie das Kästchen aktivieren, müssen Sie die Versicherung
                  nicht namentlich benennen.
                </p>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="form-input-haftpflicht-active"
                    checked={form.haftpflicht_active}
                    onChange={(e) =>
                      setMany({
                        haftpflicht_active: e.target.checked,
                        haftpflicht_name: e.target.checked
                          ? form.haftpflicht_name
                          : "",
                      })
                    }
                    className="mt-1 w-4 h-4 accent-[#d90429]"
                  />
                  <span className="text-[14px] text-[#0a1128]">
                    <strong>Eine Haftpflichtversicherung ist beteiligt.</strong>
                  </span>
                </label>
                {form.haftpflicht_active && (
                  <div className="mt-5 space-y-4">
                    <div>
                      <span className="eyebrow text-[#505a74]">
                        Versicherung (optional)
                      </span>
                      <div className="mt-2">
                        <InsuranceSelect
                          mode="haftpflicht"
                          selectedNr=""
                          selectedName={form.haftpflicht_name}
                          onSelect={(sel) => {
                            setMany({
                              haftpflicht_name: sel ? sel.name : "",
                            });
                          }}
                          idPrefix="hp"
                          placeholder="z. B. HUK, Allianz, Provinzial …"
                        />
                      </div>
                      <p className="mt-2 text-[11px] text-[#505a74]">
                        Wenn die Versicherung noch nicht feststeht, einfach
                        freilassen — wir vermerken dann nur, dass eine
                        Haftpflicht beteiligt ist.
                      </p>
                    </div>
                    <Field
                      id="haftpflicht-aktenzeichen"
                      label="Aktenzeichen / Schadennummer der Versicherung (optional)"
                      value={form.haftpflicht_aktenzeichen}
                      onChange={set("haftpflicht_aktenzeichen")}
                      placeholder="z. B. 2024/12345 HP"
                    />
                  </div>
                )}
              </Fieldset>
            )}
          </>
        )}

        {formStep === 4 && (
          <>
            {!isFirma && (
              <Fieldset
                title="Identifizierung"
                step="01"
                icon={<Shield className="w-4 h-4 text-[#d90429]" />}
              >
                <p className="text-[13px] text-[#505a74] leading-relaxed mb-5">
                  Als Rechtsanwaltskanzlei sind wir nach{" "}
                  <strong className="text-[#0a1128]">§ 11 Geldwäschegesetz (GwG)</strong>{" "}
                  verpflichtet, die Identität unserer Mandanten anhand eines
                  amtlichen Lichtbildausweises zu prüfen und festzuhalten. Wir
                  bitten Sie daher um Ihr Verständnis, ein Foto der Vorder- und
                  Rückseite Ihres Personalausweises (oder Reisepasses)
                  anzufertigen. Ihre Daten werden ausschließlich zu diesem Zweck
                  verarbeitet und unter Wahrung der anwaltlichen
                  Verschwiegenheitspflicht behandelt.
                </p>
                {isEheleute && (
                  <p className="text-[12px] text-[#505a74] mb-5">
                    Bei Eheleuten benötigen wir die Ausweisdokumente{" "}
                    <strong>beider Mandanten</strong>.
                  </p>
                )}
                <IdSummary
                  form={form}
                  isEheleute={isEheleute}
                  onOpen={() => setShowIdScanner(true)}
                />
              </Fieldset>
            )}
            <div className="bg-white border border-[#e8e6df] rounded-sm p-6 md:p-7">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="form-input-datenschutz-accept"
                  checked={datenschutzAccepted}
                  onChange={(e) => setDatenschutzAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#d90429] shrink-0"
                />
                <span className="text-[14px] text-[#0a1128] leading-relaxed">
                  <strong>Ich habe die{" "}
                    <button
                      type="button"
                      data-testid="dsgvo-open"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDsgvo(true);
                      }}
                      className="link-underline text-[#d90429] hover:text-[#0a1128] transition font-semibold"
                    >
                      Datenschutzerklärung
                    </button>{" "}
                    gelesen
                  </strong>{" "}
                  und bin mit der Verarbeitung meiner Daten zur Bearbeitung
                  meines Anliegens einverstanden.
                  <span className="block text-[#505a74] text-[12px] mt-1">
                    Pflichtangabe gemäß DSGVO. Sie können Ihre Einwilligung
                    jederzeit widerrufen.
                  </span>
                </span>
              </label>
            </div>
          </>
        )}

        <NavButtons
          step={formStep}
          onPrev={() => goToStep(formStep - 1)}
          onNext={() => goToStep(formStep + 1)}
          isLast={formStep === 4}
          submitting={submitting}
          datenschutzOk={datenschutzAccepted}
          isBestand={isBestand}
          isUnveraendert={isUnveraendert}
        />
      </form>

      {/* Modal: Hinweis gesetzliche Vertretung */}
      <Modal
        open={showLegalRepNotice}
        title="Hinweis zur gesetzlichen Vertretung"
        testIdPrefix="legal-rep-notice"
        onClose={() => {
          // X / outside / Escape → checkbox bleibt unmarkiert
          setShowLegalRepNotice(false);
        }}
        onConfirm={() => {
          setShowLegalRepNotice(false);
          setForm((f) => ({ ...f, gesetzlich_vertreten: true }));
        }}
        confirmLabel="OK"
      >
        <p className="leading-relaxed">{LEGAL_REP_NOTICE}</p>
      </Modal>

      {/* Modal: Datenschutzerklärung */}
      <Modal
        open={showDsgvo}
        title="Datenschutzerklärung"
        testIdPrefix="dsgvo"
        onClose={() => setShowDsgvo(false)}
        maxWidthClass="max-w-4xl"
      >
        <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-[#0a1128]">
          {DSGVO_TEXT}
        </div>
      </Modal>

      {/* Modal: Ausweis-Scanner */}
      <IdScannerModal
        open={showIdScanner}
        onClose={() => setShowIdScanner(false)}
        persons={buildIdPersons(form, isEheleute)}
        onCapture={(personKey, side, b64) => {
          if (personKey === "mandant") {
            setForm((f) => ({
              ...f,
              [side === "front" ? "ausweis_vorderseite" : "ausweis_rueckseite"]: b64,
            }));
          } else if (personKey === "partner") {
            setForm((f) => ({
              ...f,
              [side === "front" ? "ausweis2_vorderseite" : "ausweis2_rueckseite"]: b64,
            }));
          }
        }}
      />

      {/* Modal: Bestätigung "Ausweis später nachreichen" */}
      <Modal
        open={showMissingIdModal}
        title="Ausweisdokumente fehlen"
        testIdPrefix="missing-id"
        onClose={() => setShowMissingIdModal(false)}
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4 text-[14px] leading-relaxed">
          <p>
            Sie haben noch{" "}
            <strong className="text-[#0a1128]">keine Ausweisfotos</strong>{" "}
            hochgeladen. Nach{" "}
            <strong>§ 11 Geldwäschegesetz (GwG)</strong> sind wir zur
            Identifizierung verpflichtet.
          </p>
          <p className="text-[#505a74]">
            Möchten Sie die Aufnahme jetzt nachholen oder das Dokument später
            persönlich bzw. per E-Mail nachreichen?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-[#e8e6df]">
            <button
              type="button"
              data-testid="missing-id-capture-now"
              onClick={() => {
                setShowMissingIdModal(false);
                setShowIdScanner(true);
              }}
              className="btn-primary px-5 py-3 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-2 flex-1"
            >
              <Camera className="w-4 h-4" /> Jetzt aufnehmen
            </button>
            <button
              type="button"
              data-testid="missing-id-later"
              onClick={() => {
                setIdLater(true);
                setShowMissingIdModal(false);
                // Trigger submit again on next tick — idLater is now true
                setTimeout(() => {
                  document
                    .querySelector('[data-testid="form-submit"]')
                    ?.click();
                }, 0);
              }}
              className="btn-ghost px-5 py-3 rounded-full text-[13px] font-semibold flex-1"
            >
              Später nachreichen
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- Firma-Section 01: Firmenangaben mit inline Anschrift + Register ---------- */

const FirmaSection01 = ({
  form,
  set,
  mainAddress,
  onMainAddressChange,
  searchPlaces,
}) => (
  <div className="space-y-6">
    {/* Anrede full width */}
    <AnredeField value={form.anrede} onChange={set("anrede")} />

    {/* Firmenname */}
    <Field
      id="name"
      label="Firmenname *"
      value={form.name}
      onChange={set("name")}
      placeholder="z. B. Mustermann GmbH"
    />

    {/* Rechtsform — auto-detected, read-only display */}
    {form.rechtsform && (
      <div data-testid="form-rechtsform-detected" className="flex items-center gap-3 text-[12px] -mt-2">
        <span className="eyebrow text-[#505a74]">Erkannte Rechtsform:</span>
        <span className="font-display font-bold text-[#0a1128]">
          {form.rechtsform}
        </span>
      </div>
    )}

    {/* Firmenanschrift inline */}
    <div className="pt-2 border-t border-[#e8e6df]">
      <div className="eyebrow text-[#d90429] mb-4 flex items-center gap-2">
        <Building2 className="w-3.5 h-3.5" /> Firmenanschrift
      </div>
      <AddressBlock
        value={mainAddress}
        onChange={onMainAddressChange}
        search={searchPlaces}
        idPrefix="form-address"
        searchPlaceholder="z. B. Musterstraße 1, 50667 Köln"
      />
    </div>

    {/* Register */}
    <div className="pt-6 border-t border-[#e8e6df]">
      <div className="eyebrow text-[#505a74] mb-4">Handelsregister (optional)</div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          id="registergericht"
          label="Registergericht"
          value={form.registergericht}
          onChange={set("registergericht")}
          placeholder="z. B. Amtsgericht Siegburg"
          className="sm:col-span-2"
        />
        <Field
          id="register-art"
          label="Register-Art"
          value={form.register_art}
          onChange={set("register_art")}
          placeholder="z. B. HRB"
        />
        <Field
          id="register-nummer"
          label="Registernummer"
          value={form.register_nummer}
          onChange={set("register_nummer")}
          placeholder="z. B. 12345"
        />
      </div>
    </div>
  </div>
);

/* ---------- Re-usable subcomponents ---------- */

const buildIdPersons = (form, isEheleute) => {
  const mandantName = [form.vorname, form.name].filter(Boolean).join(" ").trim() || "Mandant";
  const persons = [
    {
      key: "mandant",
      label: isEheleute ? `Person 1 — ${mandantName}` : mandantName,
      front: form.ausweis_vorderseite || "",
      back: form.ausweis_rueckseite || "",
    },
  ];
  if (isEheleute) {
    const p2 = form.partner_person || {};
    const partnerName = [p2.vorname, p2.name].filter(Boolean).join(" ").trim() || "Person 2";
    persons.push({
      key: "partner",
      label: `Person 2 — ${partnerName}`,
      front: form.ausweis2_vorderseite || "",
      back: form.ausweis2_rueckseite || "",
    });
  }
  return persons;
};

const IdSummary = ({ form, isEheleute, onOpen }) => {
  const persons = buildIdPersons(form, isEheleute);
  const allCaptured = persons.every((p) => p.front && p.back);
  return (
    <div className="space-y-4" data-testid="id-summary">
      <div className="grid sm:grid-cols-2 gap-3">
        {persons.map((p) => (
          <div
            key={p.key}
            data-testid={`id-summary-${p.key}`}
            className="border border-[#e8e6df] rounded-sm p-4 bg-[#fafaf7]"
          >
            <div className="text-[12px] font-semibold text-[#0a1128] mb-3">
              {p.label}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <IdThumb label="Vorderseite" b64={p.front} />
              <IdThumb label="Rückseite" b64={p.back} />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onOpen}
        data-testid="id-summary-open"
        className={`px-5 py-3 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 ${
          allCaptured ? "btn-ghost" : "btn-primary"
        }`}
      >
        <Camera className="w-4 h-4" />
        {allCaptured ? "Ausweisaufnahmen ändern" : "Ausweis jetzt aufnehmen"}
      </button>
      {!allCaptured && (
        <p className="text-[11px] text-[#505a74]">
          Sie können das Formular auch ohne Ausweisbilder absenden — wir bitten
          dann jedoch um zeitnahe Nachreichung.
        </p>
      )}
    </div>
  );
};

const IdThumb = ({ label, b64 }) => (
  <div className="bg-white border border-[#e8e6df] rounded-sm overflow-hidden">
    <div className="text-[10px] font-semibold tracking-wider uppercase text-[#505a74] px-2 py-1 border-b border-[#e8e6df]">
      {label}
    </div>
    <div className="aspect-[1.586/1] bg-[#f4f3ed] flex items-center justify-center">
      {b64 ? (
        <img
          src={b64}
          alt={label}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-[11px] text-[#a8a89a]">— noch nicht aufgenommen —</span>
      )}
    </div>
  </div>
);

const Fieldset = ({ title, step, icon, children }) => (
  <section className="bg-white border border-[#e8e6df] rounded-sm p-6 md:p-8">
    <div className="flex items-center gap-3 mb-6">
      <span className="text-[11px] font-mono text-[#d90429] tracking-widest">
        {step}
      </span>
      <h2 className="font-display text-[20px] md:text-[22px] font-bold tracking-tight flex items-center gap-2">
        {icon}
        {title}
      </h2>
    </div>
    {children}
  </section>
);

const Field = ({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  className = "",
  inputMode,
}) => {
  const isTel = type === "tel";
  const im = inputMode || (isTel ? "tel" : undefined);
  let pattern;
  if (isTel) pattern = "[0-9+\\s\\-()/]*";
  else if (im === "numeric") pattern = "[0-9]*";
  else if (im === "decimal") pattern = "[0-9.,]*";
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow text-[#505a74]">{label}</span>
      <input
        type={type}
        inputMode={im}
        pattern={pattern}
        value={value || ""}
        data-testid={`form-input-${id}`}
        onChange={(e) => {
          const v = type === "date" ? clipDateYear(e.target.value) : e.target.value;
          onChange(v);
        }}
        max={type === "date" ? "9999-12-31" : undefined}
        placeholder={placeholder}
        className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
      />
    </label>
  );
};

const AnredeField = ({ value, onChange }) => {
  const isCustom = value !== "" && !ANREDEN.includes(value);
  const [custom, setCustom] = useState(isCustom);
  return (
    <div className="sm:col-span-2">
      <span className="eyebrow text-[#505a74]">Anrede *</span>
      <div className="mt-2 flex flex-wrap gap-2">
        {ANREDEN.map((a) => (
          <button
            key={a}
            type="button"
            data-testid={`form-anrede-${a.toLowerCase()}`}
            onClick={() => {
              setCustom(false);
              onChange(a);
            }}
            className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition ${
              value === a && !custom
                ? "bg-[#0a1128] text-white border-[#0a1128]"
                : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
            }`}
          >
            {a}
          </button>
        ))}
        <button
          type="button"
          data-testid="form-anrede-custom-toggle"
          onClick={() => {
            setCustom(true);
            onChange("");
          }}
          className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition ${
            custom
              ? "bg-[#0a1128] text-white border-[#0a1128]"
              : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
          }`}
        >
          Eigene…
        </button>
      </div>
      {custom && (
        <input
          type="text"
          data-testid="form-anrede-custom-input"
          value={value}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          placeholder="Eigene Anrede eingeben"
          className="mt-3 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
        />
      )}
    </div>
  );
};

/* ---------- Success Screen ---------- */

const SuccessScreen = ({ data, isBestand, onReset }) => (
  <div
    data-testid="form-success"
    className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
  >
    <div className="inline-flex w-16 h-16 rounded-full bg-[#0a1128] text-white items-center justify-center">
      <CheckCircle2 className="w-8 h-8 text-[#d90429]" />
    </div>
    <h1 className="mt-8 font-display text-[40px] md:text-[56px] font-bold leading-[1.05] tracking-tight">
      {isBestand ? "Änderung erfasst." : "Daten übermittelt."}
    </h1>
    <p className="mt-5 text-[#505a74] text-[15px] leading-relaxed max-w-md mx-auto">
      Ihre Daten wurden übermittelt.
    </p>

    <button
      onClick={() => window.location.reload()}
      data-testid="form-reset"
      className="mt-12 btn-ghost px-7 py-4 rounded-full text-[13px] font-semibold"
    >
      Neue Anfrage starten
    </button>
  </div>
);

/* ---------- Wizard chrome ---------- */

const STEP_LABELS_DEFAULT = ["Stammdaten", "Anliegen", "Gegenseite"];

const StepProgress = ({ current, labels = STEP_LABELS_DEFAULT }) => (
  <div data-testid="wizard-progress" className="mt-8 flex items-center gap-3">
    {labels.map((label, i) => {
      const n = i + 1;
      const active = current === n;
      const done = current > n;
      return (
        <div key={label} className="flex items-center gap-3 flex-1">
          <div
            className={`flex items-center gap-2 ${
              active ? "" : done ? "text-[#0a1128]" : "text-[#505a74]"
            }`}
          >
            <span
              className={`w-7 h-7 inline-flex items-center justify-center rounded-full text-[12px] font-bold transition ${
                active
                  ? "bg-[#d90429] text-white"
                  : done
                    ? "bg-[#0a1128] text-white"
                    : "bg-white border border-[#e8e6df] text-[#505a74]"
              }`}
            >
              {done ? "✓" : n}
            </span>
            <span
              className={`text-[12px] font-semibold tracking-wider uppercase ${
                active
                  ? "text-[#0a1128]"
                  : done
                    ? "text-[#0a1128]"
                    : "text-[#505a74]"
              }`}
            >
              {label}
            </span>
          </div>
          {n < labels.length && (
            <span
              className={`h-px flex-1 ${
                done ? "bg-[#0a1128]" : "bg-[#e8e6df]"
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

const NavButtons = ({
  step,
  onPrev,
  onNext,
  isLast,
  submitting,
  datenschutzOk,
  isBestand,
  isUnveraendert,
}) => {
  const labelLast = isBestand
    ? "Änderung verbindlich senden"
    : isUnveraendert
      ? "Anfrage verbindlich senden"
      : "Daten verbindlich senden";
  return (
    <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-[#e8e6df]">
      <div>
        {step > 1 && (
          <button
            type="button"
            onClick={onPrev}
            data-testid="wizard-prev"
            className="text-[13px] font-semibold link-underline inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        {!isLast && (
          <button
            type="button"
            onClick={onNext}
            data-testid="wizard-next"
            className="btn-primary px-7 py-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
          >
            Weiter
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {isLast && (
          <button
            type="submit"
            disabled={submitting || !datenschutzOk}
            data-testid="form-submit"
            className="btn-primary px-7 py-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? "Wird gesendet..." : labelLast}
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

